import { Component, OnDestroy, inject } from "@angular/core";
import { registerLocaleData } from "@angular/common";
import localeDe from "@angular/common/locales/de";
import { TranslateService } from "@ngx-translate/core";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";
import {
	CHANGE_ADDRESS_TARGET,
	PageHeaderComponent,
	PageHeaderMenuEntry,
} from "libs/ux-components/src/lib/container/page-header/page-header.component";
import { PageFooterComponent } from "libs/ux-components/src/lib/components/page-footer/page-footer.component";
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { NotificationService } from "libs/tm-common/src/lib/services/notification.lib.service";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { FeatureFlagsService } from "libs/feature-flags/src/lib/services/feature-flags.lib.service";
import { FeatureFlag } from "libs/feature-flags/src/lib/models/feature-flags.model";
import { of, Subscription, switchMap } from "rxjs";
import { SettingsService } from "libs/settings/src/lib/services/settings.lib.service";
import { ResponsiveDialogAdapter } from "libs/ux-components/src/lib/adapters/responsive-dialog-adapter";
import { MatDialog } from "@angular/material/dialog";

const LOCALE_STORAGE_ITEM = "locale";

@Component({
	selector: "tm-root",
	templateUrl: "./app.tm.component.html",
	styleUrls: ["./_app.tm.component.scss"],
	imports: [RouterOutlet, PageHeaderComponent, PageFooterComponent],
	host: {
		"(window:scroll)": "onScroll()",
	},
	providers: [{provide: MatDialog, useClass: ResponsiveDialogAdapter}]
})
export class AppComponent implements OnDestroy {
	private translate = inject(TranslateService);
	private notification = inject(NotificationService);
	private featureFlagService = inject(FeatureFlagsService);
	private settingsService = inject(SettingsService);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);
	private route = inject(ActivatedRoute);
	snackBar = inject(MatSnackBar);

	translateSubscription: Subscription;
	etrackerFlagSubscription: Subscription;
	queryParamsSubscription: Subscription;
	isEtrackerInitialized: boolean = false;
	isScrolled = false;

	onScroll() {
		this.isScrolled = window.scrollY > 0;
	}

	// for notification service - central snackbar
	notificationSubscription: Subscription;
	sbConfig = new MatSnackBarConfig();
	snackBarRef;

	//SEPA constants
	sepaFormTitle = "SEPA Form";
	sepaPdfDE = "/static/media/SCHEU_SEPA_de.pdf";
	sepaPdfEN = "/static/media/SCHEU_SEPA_en.pdf";

	// menu definitions for page-header
	menuEntries: PageHeaderMenuEntry[] = [
		{ title: "Dashboard", route: "/dashboard" },
		{ title: "PromotionsHeader", route: "/promotions" },
		{ title: "Help", route: "/help" },
	];

	subMenuEntries: PageHeaderMenuEntry[] = [
		{ title: this.sepaFormTitle, url: this.sepaPdfDE },
		{ title: "CustomerMasterDataChange", route: CHANGE_ADDRESS_TARGET },
		{ title: "Contact", url: "mailto:info@scheu-dental.com" },
	];

	constructor() {
		// register locale and set default language
		registerLocaleData(localeDe, "de");
		let locale = this.config.locale;
		if (["de", "en"].includes(localStorage.getItem(LOCALE_STORAGE_ITEM))) {
			locale = localStorage.getItem(LOCALE_STORAGE_ITEM);
		}
		localStorage.setItem(LOCALE_STORAGE_ITEM, locale);
		this.translate.setFallbackLang(locale);
		this.translate.use(locale);

		// subscribe language changes
		this.translateSubscription = this.translate.onLangChange.subscribe((e) => {
			this.config.locale = e.lang;
			localStorage.setItem(LOCALE_STORAGE_ITEM, this.config.locale);
			this.subMenuEntries.find((entry) => entry.title === this.sepaFormTitle).url =
				this.config.locale === "de" ? this.sepaPdfDE : this.sepaPdfEN;
		});

		// subscribe notification service
		this.notificationSubscription = this.notification.notify.subscribe((data) =>
			this.handleNotification(data)
		);

		this.etrackerFlagSubscription = this.featureFlagService
			.isFeatureEnabled$(FeatureFlag.ENABLE_ETRACKER)
			.pipe(
				switchMap((enabled: boolean) => {
					return enabled ? this.settingsService.fetchEtrackerSecureCode() : of(null);
				})
			)
			.subscribe((data: { secureCode: string }) => {
				if (data?.secureCode && !this.isEtrackerInitialized) {
					this.initEtracker(data.secureCode);
				}
			});

		// subscribe language changes from query params
		this.queryParamsSubscription = this.route.queryParams.subscribe((params) => {
			if (["de", "en"].includes(params["lang"])) {
				this.translate.use(params["lang"]);
			}
		});
	}

	ngOnDestroy() {
		this.translateSubscription.unsubscribe();
		this.notificationSubscription.unsubscribe();
		this.queryParamsSubscription.unsubscribe();
		this.etrackerFlagSubscription.unsubscribe();
	}

	handleNotification(data) {
		this.sbConfig.verticalPosition = "top";
		this.sbConfig.panelClass = [`sb-${data.type}`];
		this.sbConfig.duration = data.duration ? data.duration : this.sbConfig.duration;
		this.snackBarRef = this.snackBar.open(data.message, this.translate.instant("close"), this.sbConfig);
	}

	/**
	 * Adds the etracker script to the document head.
	 * See https://help.etracker.com/en/article/single-page-applications-measurements/
	 * for reference.
	 */
	private initEtracker(etrackerSecureCode: string) {
		const script = document.createElement("script");
		script.async = true;
		script.id = "_etLoader";
		script.type = "text/javascript";
		script.src = "//code.etracker.com/code/e.js";
		script.setAttribute("data-block-cookies", "true");
		script.setAttribute("data-page-changed-detection", "hash");
		script.setAttribute("data-secure-code", etrackerSecureCode);
		document.head.appendChild(script);
		this.isEtrackerInitialized = true;
	}
}
